import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaSearch,
  FaGraduationCap,
  FaSchool,
  FaCheck,
  FaExclamationTriangle,
  FaDownload,
  FaArrowRight,
  FaLock,
  FaMobileAlt,
  FaEdit,
  FaBolt,
  FaLeaf,
  FaUsers,
  FaCamera,
  FaSpinner,
} from "react-icons/fa";
import { useLookupBricsSchoolMutation } from "../redux/auth/authApi";
import BricsCampaignModal from "../Components/SchoolAdmin/BricsCampaign/BricsCampaignModal";
import { BSLW_POSTERS, BSLW_POSTER_PACK } from "../Components/SchoolAdmin/BricsCampaign/bslwPosters";
import mainPosterImg from "../assets/images/brics_campaign_main_poster.jpg";
import "./BricsSustainableLifestyleWeek.css";

const UDISE_LEN = 11;
// A UDISE code is 11 characters and is NOT always all digits — some carry
// letters, and stripping them silently ate the character the school typed.
// Upper-cased here because that is the spelling the server keys rows by.
const normalizeUdise = (v) =>
  String(v || "")
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, UDISE_LEN);

// What the 8-question form asks, so a school can gather it before opening the
// form. Q3 and Q4 are answered from the school's own records where we have
// them, which is why they are marked as fetched rather than as something to
// bring along.
const READY_ITEMS = [
  { n: "Q1", tone: "q1", icon: <FaBolt />, text: "One-hour switch-off (lights, fans, etc.) held daily for a week", pill: "Yes / No" },
  { n: "Q2", tone: "q2", icon: <FaBolt />, text: "September electricity consumption from your bill (up to 10,000)", pill: "Units or kWh" },
  { n: "Q3", tone: "q3", icon: <FaLeaf />, text: "Four-bin segregation, with the colour and waste category of each bin", pill: "Fetched where known" },
  { n: "Q4", tone: "q4", icon: <FaLock />, text: "Eco Club notification on the notice board", pill: "Fetched where known" },
  { n: "Q5", tone: "q1", icon: <FaLeaf />, text: "Posters, awareness material and the 7 themes on the notice board", pill: "Yes / No" },
  { n: "Q6", tone: "q2", icon: <FaLeaf />, text: "Activities held on the Mission LiFE themes — poster making, painting, rangoli, slogan writing or others", pill: "Tick all that apply" },
  { n: "Q7", tone: "q3", icon: <FaUsers />, text: "Students and teachers who took part (up to 1,000 each)", pill: "Counts" },
  { n: "Q8", tone: "q4", icon: <FaCamera />, text: "2 to 5 geotagged photographs of the activities", pill: "Photos" },
];

export default function BricsSustainableLifestyleWeek() {
  const navigate = useNavigate();
  const authUser = useSelector((state) => state?.auth?.user) || {};
  const [lookupBricsSchool, { isLoading: isLookingUp }] = useLookupBricsSchoolMutation();

  const [udise, setUdise] = useState(normalizeUdise(authUser?.udise_id) || "");
  const [lookState, setLookState] = useState("idle"); // idle | loading | found | error
  const [lookMsg, setLookMsg] = useState("");
  const [look, setLook] = useState(null); // { school, report, status }
  const [isModalOpen, setIsModalOpen] = useState(false);

  const inputRef = useRef(null);
  const lastCode = useRef("");

  // Auto-focus input on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  // If user is already logged in as school admin, auto-lookup
  useEffect(() => {
    if (authUser?.udise_id && /^\d{11}$/.test(String(authUser.udise_id).trim())) {
      const code = String(authUser.udise_id).trim();
      setUdise(code);
      triggerLookup(code);
    }
  }, [authUser?.udise_id]);

  const triggerLookup = async (codeToSearch) => {
    const code = normalizeUdise(codeToSearch || udise);
    if (!code || code.length !== UDISE_LEN) {
      setLookState("error");
      setLookMsg("Please enter a valid 11-character school UDISE code.");
      return;
    }

    lastCode.current = code;
    setLookState("loading");
    setLookMsg("");
    setLook(null);

    try {
      const res = await lookupBricsSchool({ udise_id: code }).unwrap();
      if (lastCode.current !== code) return;

      if (!res?.found) {
        setLookState("error");
        setLookMsg(res?.message || "We could not find a school with this UDISE code. Please check the 11 digits and try again.");
        return;
      }

      setLook({
        school: res.school,
        report: res.report || null,
        // Q3/Q4 of the School Reporting form, read from the school's own
        // records — the lookup carries them so the form opens already answered.
        prefill: res.prefill || null,
        status: res.status || {},
      });
      setLookState("found");
    } catch (err) {
      if (lastCode.current !== code) return;
      setLookState("error");
      setLookMsg(
        err?.data?.message || err?.message || "Could not reach the server to verify this UDISE code. Please try again."
      );
    }
  };

  const handleInputChange = (e) => {
    const val = normalizeUdise(e.target.value);
    setUdise(val);
    if (lookState !== "idle") {
      setLookState("idle");
      setLookMsg("");
      setLook(null);
    }
    // Auto search when 11 digits are entered
    if (val.length === UDISE_LEN) {
      triggerLookup(val);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      triggerLookup(udise);
    }
  };

  const handleChangeCode = () => {
    setUdise("");
    setLook(null);
    setLookState("idle");
    setLookMsg("");
    inputRef.current?.focus();
  };

  const handleOpenForm = () => {
    if (!look?.school) {
      toast.warning("Please verify your school UDISE code first.");
      return;
    }
    setIsModalOpen(true);
  };

  const handleModalCompleted = () => {
    // Re-trigger lookup to refresh report status
    if (udise) {
      triggerLookup(udise);
    }
  };

  const hasSubmitted =
    look?.report?.saveEnergy?.status === "submitted" ||
    look?.report?.adoptHealthyLifestyle?.status === "submitted";

  return (
    <div className="brics-page">
      {/* ── Top Header Band ── */}
      <div className="brics-page__band">
        <div className="brics-page__band-inner">
          {/* Left: Titles & Stepper */}
          <div className="brics-page__band-copy">
            <span className="brics-page__badge">
              <span className="brics-page__badge-dot" />
              <span>UNDER BRICS SUSTAINABLE LIFESTYLE WEEK 2026</span>
            </span>

            <h1 className="brics-page__title">
              BRICS Sustainable Lifestyle Week 2026
            </h1>

            <p className="brics-page__subtitle">
              One Week, Seven Actions, One Planet
            </p>

            {/* Stepper Progress Indicator */}
            <div className="brics-stepper" role="list" aria-label="Submission steps">
              <div className="brics-stepper__item is-active" role="listitem">
                <span className="brics-stepper__num">1</span>
                <span className="brics-stepper__label">School UDISE ID</span>
              </div>
              <span className="brics-stepper__line" aria-hidden="true" />
              <div className="brics-stepper__item" role="listitem">
                <span className="brics-stepper__num">2</span>
                <span className="brics-stepper__label">Participation details</span>
              </div>
              <span className="brics-stepper__line" aria-hidden="true" />
              <div className="brics-stepper__item" role="listitem">
                <span className="brics-stepper__num">3</span>
                <span className="brics-stepper__label">Submit</span>
              </div>
            </div>
          </div>

          {/* Right: Campaign Banner Thumbnail & Download Button */}
          <div className="brics-page__banner-card">
            <img
              src={mainPosterImg}
              alt="BRICS Sustainable Lifestyle Week 2026 Poster"
              className="brics-page__banner-thumb"
            />
            <a
              href={mainPosterImg}
              download="BRICS_Sustainable_Lifestyle_Week_2026_Poster.jpg"
              className="brics-page__download-btn"
              title="Download high-resolution campaign banner for your school"
            >
              <FaDownload />
              <span>Download this banner</span>
            </a>
          </div>
        </div>
      </div>

      {/* ── Step 1 Card: Enter School UDISE ID ── */}
      <main className="brics-page__main">
        <div className="brics-card">
          <div className="brics-card__header">
            <div className="brics-card__icon-badge" aria-hidden="true">
              <FaGraduationCap />
            </div>
            <div className="brics-card__step-tag">STEP 1 OF 3</div>
            <h2 className="brics-card__title">Enter School UDISE ID</h2>
            <p className="brics-card__sub">
              Your school’s name, block and district will appear automatically.
            </p>
          </div>

          {/* Search Bar Input */}
          <div className="brics-search-wrap">
            <div className="brics-search-bar">
              <FaSearch className="brics-search-icon" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                spellCheck="false"
                autoComplete="off"
                className="brics-search-input"
                placeholder="11-character UDISE code"
                maxLength={UDISE_LEN}
                value={udise}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                aria-label="11-character School UDISE code"
              />
              <button
                type="button"
                className="brics-search-btn"
                onClick={() => triggerLookup(udise)}
                disabled={isLookingUp || udise.length !== UDISE_LEN}
              >
                {isLookingUp ? "Searching…" : "Search"}
              </button>
            </div>
            <div className="brics-search-counter">
              {`${udise.length} of ${UDISE_LEN} characters`}
            </div>
          </div>

          {/* Status & Feedback Area */}
          <div className="brics-status-zone" aria-live="polite">
            {isLookingUp && (
              <div className="brics-status-loading">
                <FaSpinner className="fa-spin" />
                <span>Finding your school details…</span>
              </div>
            )}

            {lookState === "error" && (
              <div className="brics-status-error" role="alert">
                <FaExclamationTriangle style={{ flexShrink: 0, marginTop: "2px" }} />
                <span>{lookMsg}</span>
              </div>
            )}

            {lookState === "found" && look?.school && (
              <>
                <div className="brics-school-card">
                  <div className="brics-school-badge" aria-hidden="true">
                    <FaSchool />
                  </div>
                  <div className="brics-school-body">
                    <span className="brics-school-eyebrow">School found</span>
                    <div className="brics-school-name">{look.school.school_name}</div>
                    <div className="brics-school-meta">
                      {[look.school.block, look.school.district, look.school.state].filter(Boolean).join(", ")}
                    </div>
                    <span className="brics-school-udise">UDISE {udise}</span>
                  </div>
                  <div className="brics-school-tick" aria-hidden="true">
                    <FaCheck />
                  </div>
                </div>

                {hasSubmitted && (
                  <div className="brics-already-box">
                    <FaCheck style={{ color: "#0d9488" }} />
                    <span>
                      This school has previously submitted initiatives under BRICS Sustainable Lifestyle Week. You can review and update your report.
                    </span>
                  </div>
                )}

                {/* Open Form Button */}
                <div className="brics-action-row">
                  <button
                    type="button"
                    className="brics-btn-open"
                    onClick={handleOpenForm}
                  >
                    <span>{hasSubmitted ? "Review & Update Form" : "Open the Form"}</span>
                    <FaArrowRight />
                  </button>

                  <button
                    type="button"
                    className="brics-btn-link"
                    onClick={handleChangeCode}
                  >
                    Not your school? Change the code
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ── Campaign material, under the UDISE box: optional, for any school
                 that wants to print and display it (Q5 asks about exactly this) ── */}
          <div className="brics-iec">
            <div className="brics-iec__head">
              <div>
                <h3 className="brics-iec__title">Campaign material</h3>
                <p className="brics-iec__sub">
                  Optional — print these and put them up on the school notice board.
                </p>
              </div>
              <a className="brics-iec__all" href={BSLW_POSTER_PACK} download>
                <FaDownload /> Download all (PDF)
              </a>
            </div>
            <div className="brics-iec__strip">
              {BSLW_POSTERS.map((poster) => (
                <a
                  key={poster.key}
                  className="brics-iec__item"
                  href={poster.file}
                  download={`BSLW-${poster.key}.jpg`}
                  title={`Download — ${poster.title}`}
                >
                  <img src={poster.thumb} alt={poster.title} loading="lazy" />
                  <span className="brics-iec__cap">
                    <FaDownload /> {poster.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* ── Keep These Ready Section ── */}
          <div className="brics-ready-zone">
            <div className="brics-ready-header">
              <h3 className="brics-ready-title">Keep these ready</h3>
              <span className="brics-ready-subtitle">Eight questions — all of them required</span>
            </div>

            <div className="brics-ready-grid">
              {READY_ITEMS.map((item) => (
                <div className={`brics-ready-card brics-ready-card--${item.tone}`} key={item.n}>
                  <div className="brics-ready-card__top">
                    <span className="brics-ready-card__icon">{item.icon}</span>
                    <span className="brics-ready-card__num">{item.n}</span>
                  </div>
                  <div className="brics-ready-card__text">{item.text}</div>
                  <span className="brics-ready-card__pill">{item.pill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Footer Reassurance ── */}
          <div className="brics-reassurance-row">
            <div className="brics-reassurance-item">
              <FaLock />
              <span>No login needed</span>
            </div>
            <div className="brics-reassurance-item">
              <FaMobileAlt />
              <span>Works on mobile phones</span>
            </div>
            <div className="brics-reassurance-item">
              <FaEdit />
              <span>Submitted details can be updated</span>
            </div>
          </div>
        </div>
      </main>

      {/* ── BRICS Campaign Modal Pop-up ── */}
      <BricsCampaignModal
        key={udise}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        schoolData={look?.school}
        udiseId={udise}
        publicReport={look ? { report: look.report || null, prefill: look.prefill || null, status: look.status || null } : null}
        onCompleted={handleModalCompleted}
      />
    </div>
  );
}

